import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { Button } from '@/components/generals/button'
import { Reveal } from '@/components/generals/reveal'
import { Section } from '@/components/generals/section'
import { gsap, registerGsap, prefersReducedMotion, useIsomorphicLayoutEffect } from '@/lib/motion'

/**
 * The statement block. In the original this paragraph sits behind a 10px blur
 * and resolves as the section travels up the viewport, so the reveal is tied to
 * scroll position rather than played once on entry.
 */
export const AboutStatement: FC = (): ReactNode => {
    const textRef = useRef<HTMLParagraphElement | null>(null)

    useIsomorphicLayoutEffect(() => {
        const node = textRef.current

        if (!node || prefersReducedMotion()) {
            return
        }

        registerGsap()

        const context = gsap.context(() => {
            gsap.fromTo(
                node,
                {
                    filter: 'blur(10px)',
                    opacity: 0.35,
                    scale: 0.985
                },
                {
                    filter: 'blur(0px)',
                    opacity: 1,
                    scale: 1,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: node,
                        start: 'top 85%',
                        end: 'center 58%',
                        scrub: 0.6
                    }
                }
            )
        }, node)

        return () => context.revert()
    }, [])

    return (
        <Section id='about-us'>
            <div className='flex w-full flex-col items-center gap-10'>
                <p
                    ref={textRef}
                    id='about-us-text'
                    className='mx-auto max-w-[24ch] text-center text-[clamp(1.625rem,3.6vw,3rem)] leading-[1.15] font-medium tracking-[-0.04em] text-white'
                >
                    Here at Funnel, we focus on returns. We&rsquo;re dedicated to scaling your brand with paid advertising. Break free and take your brand to next level.
                </p>

                <Reveal
                    y={18}
                    blur={8}
                    delay={0.1}
                >
                    <Button href='/contact'>
                        Book a 15-min call
                    </Button>
                </Reveal>
            </div>
        </Section>
    )
}
